import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import {
  Activity,
  BookOpen,
  CircleDot,
  ExternalLink,
  Filter,
  Globe2,
  Pause,
  Play,
  RefreshCw,
  Search,
  Shield,
  Sparkles,
  Layers3,
  LoaderCircle,
  Maximize2,
  Minimize2,
  Target,
  Waypoints,
  ZoomIn,
  ZoomOut,
} from 'lucide-react';
import { geoDistance, geoGraticule10, geoOrthographic, geoPath } from 'd3-geo';
import type { GeoPermissibleObjects } from 'd3-geo';
import { feature } from 'topojson-client';
import type { Feature, FeatureCollection, Geometry } from 'geojson';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - Vite + resolveJsonModule handle this package JSON correctly.
import worldTopo from 'world-atlas/countries-110m.json';
import { apiFetch } from '../lib/apiClient';
import { useAuth } from '../context/AuthContext';
import { ThreatLandscapeOperationalPanel } from './ThreatLandscapeOperationalPanel';
import { ThreatLandscapeIntelligenceGraph } from './ThreatLandscapeIntelligenceGraph';

interface LandscapeActorSummary {
  id: string;
  name: string;
  mitreId: string;
  aliases: string[];
  nation: string;
  nationCode: string;
  attributionAnchor: { lat: number; lon: number; label: string };
  motivations: string[];
  confidence: string;
  summary: string;
  targetSectors: string[];
  targetRegions: string[];
  techniqueCount: number;
  softwareCount: number;
  cveCount: number;
  sourceCount: number;
  lastReviewedUtc: string;
}

interface LandscapeSummary {
  trackedActors: number;
  attributedNations: number;
  uniqueTechniques: number;
  softwareRelationships: number;
  cveRelationships: number;
  sourceRecords: number;
  campaigns?: number;
  rawStixObjects?: number;
  cisaKevRecords?: number;
  microsoftArticles?: number;
  kloudSecureVulnerabilities?: number;
  datasetMode: string;
  lastReviewedUtc: string;
}

interface LandscapeSyncSource {
  sourceKey: string;
  displayName: string;
  status: string;
  mode: string;
  endpoint: string;
  lastSyncAt: string | null;
  lastSuccessAt: string | null;
  lastError: string | null;
  objectCount: number;
  metadata: Record<string, unknown>;
}

interface LandscapeSyncInfo {
  sources: LandscapeSyncSource[];
  lastSuccessfulSync: string | null;
  cacheMode: string;
}

interface Technique { id: string; name: string; sourceIds: string[] }
interface Software { name: string; kind: 'Malware' | 'Tool' | 'Infrastructure'; sourceIds: string[] }
interface Cve { id: string; product: string; note: string; sourceIds: string[] }
interface ActorDetail extends Omit<LandscapeActorSummary, 'techniqueCount' | 'softwareCount' | 'cveCount' | 'sourceCount'> {
  techniques: Technique[];
  software: Software[];
  cves: Cve[];
  sourceIds: string[];
}
interface LandscapeSource {
  id: string;
  publisher: string;
  title: string;
  url: string;
  sourceType: string;
  tlp: string;
}
interface LandscapeResponse { ok: true; summary: LandscapeSummary; actors: LandscapeActorSummary[]; sync?: LandscapeSyncInfo; notice?: string }
interface ActorResponse { ok: true; actor: ActorDetail }
interface RelationshipsResponse { ok: true; actor: { id: string; name: string; mitreId: string }; techniques: Technique[]; software: Software[]; cves: Cve[] }
interface SourcesResponse { ok: true; sources: LandscapeSource[] }

type CountryFeatureProps = { name?: string };
type CountryFeature = Feature<Geometry, CountryFeatureProps>;
type WorldFC = FeatureCollection<Geometry, CountryFeatureProps>;

const WORLD: WorldFC = (() => {
  const topo = worldTopo as unknown as Parameters<typeof feature>[0];
  return feature(
    topo,
    (topo as unknown as { objects: { countries: unknown } }).objects.countries as never,
  ) as unknown as WorldFC;
})();

// Keep the high-detail geography in a module-level cache so switching away from
// Immersive mode and back does not parse the 50m topology again. The promise is
// also shared so hover pre-warming and a click cannot trigger duplicate imports.
let immersiveWorldCache: WorldFC | null = null;
let immersiveWorldPromise: Promise<WorldFC> | null = null;

function loadImmersiveWorld(): Promise<WorldFC> {
  if (immersiveWorldCache) return Promise.resolve(immersiveWorldCache);
  if (!immersiveWorldPromise) {
    immersiveWorldPromise = import('world-atlas/countries-50m.json')
      .then((mod) => {
        const topo = mod.default as unknown as Parameters<typeof feature>[0];
        const world = feature(
          topo,
          (topo as unknown as { objects: { countries: unknown } }).objects.countries as never,
        ) as unknown as WorldFC;
        immersiveWorldCache = world;
        return world;
      })
      .catch((error) => {
        immersiveWorldPromise = null;
        throw error;
      });
  }
  return immersiveWorldPromise;
}

type GlobeMode = 'standard' | 'immersive';

const REGION_ANCHORS: Array<{ keys: string[]; point: [number, number] }> = [
  { keys: ['north america', 'united states', 'usa', 'canada'], point: [-99, 40] },
  { keys: ['south america', 'latin america'], point: [-60, -15] },
  { keys: ['europe', 'european union', 'nato'], point: [15, 51] },
  { keys: ['ukraine'], point: [31, 49] },
  { keys: ['middle east'], point: [44, 29] },
  { keys: ['africa'], point: [20, 4] },
  { keys: ['east asia'], point: [116, 34] },
  { keys: ['south asia'], point: [78, 22] },
  { keys: ['southeast asia'], point: [106, 12] },
  { keys: ['asia'], point: [90, 34] },
  { keys: ['australia', 'oceania'], point: [134, -25] },
  { keys: ['global', 'worldwide'], point: [0, 12] },
];

function targetRegionAnchor(region: string): [number, number] | null {
  const normalized = region.toLowerCase();
  return REGION_ANCHORS.find((item) => item.keys.some((key) => normalized.includes(key)))?.point ?? null;
}

const IMMERSIVE_STARS = Array.from({ length: 96 }, (_, index) => {
  const x = ((Math.sin(index * 91.17) + 1) / 2) * 760;
  const y = ((Math.cos(index * 37.71) + 1) / 2) * 600;
  const r = 0.35 + ((index * 17) % 10) / 12;
  const opacity = 0.18 + ((index * 23) % 10) / 14;
  return { x, y, r, opacity };
});

const W = 760;
const H = 600;
const MIN_SCALE = 205;
const MAX_SCALE = 335;
const COUNTRY_FILL = '#0d1b2a';
const COUNTRY_STROKE = '#29415f';
const GRATICULE_STROKE = '#173552';
const NATION_FILL = '#0f3352';

function fmtDate(value?: string): string {
  if (!value) return '-';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleString();
}

function shortText(value: string, max = 90): string {
  return value.length <= max ? value : `${value.slice(0, max - 1)}…`;
}

function motifClass(motivation: string): string {
  const v = motivation.toLowerCase();
  if (v.includes('destructive')) return 'border-rose-400/30 bg-rose-500/10 text-rose-200';
  if (v.includes('financial')) return 'border-amber-400/30 bg-amber-500/10 text-amber-200';
  if (v.includes('strategic')) return 'border-violet-400/30 bg-violet-500/10 text-violet-200';
  return 'border-cyan-400/30 bg-cyan-500/10 text-cyan-200';
}

function nationMarkerClass(nation: string): string {
  if (nation === 'Russia') return '#fb7185';
  if (nation === 'China') return '#fbbf24';
  if (nation === 'North Korea') return '#a78bfa';
  if (nation === 'Iran') return '#34d399';
  return '#38bdf8';
}

export function ThreatLandscapeTab() {
  const { canCapability } = useAuth();
  const canDossier = canCapability('threat_intel', 'view_actor_dossier');
  const canRelationships = canCapability('threat_intel', 'view_threat_relationships');
  const canIntelligenceGraph = canCapability('threat_intel', 'view_intelligence_graph');
  const canActorCampaigns = canCapability('threat_intel', 'view_actor_campaigns');
  const canThreatFeed = canCapability('threat_intel', 'view_threat_feed');
  const canSources = canCapability('threat_intel', 'view_source_attribution');
  const canImmersiveGlobe = canCapability('threat_intel', 'use_immersive_globe');

  const [summary, setSummary] = useState<LandscapeSummary | null>(null);
  const [actors, setActors] = useState<LandscapeActorSummary[]>([]);
  const [syncInfo, setSyncInfo] = useState<LandscapeSyncInfo | null>(null);
  const [selectedId, setSelectedId] = useState('');
  const [actorDetail, setActorDetail] = useState<ActorDetail | null>(null);
  const [relationships, setRelationships] = useState<RelationshipsResponse | null>(null);
  const [sources, setSources] = useState<LandscapeSource[]>([]);
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [nation, setNation] = useState('All');
  const [motivation, setMotivation] = useState('All');
  const [globeMode, setGlobeMode] = useState<GlobeMode>(() => localStorage.getItem('ks-threat-landscape-globe-mode') === 'immersive' ? 'immersive' : 'standard');
  const [immersiveWorld, setImmersiveWorld] = useState<WorldFC | null>(() => immersiveWorldCache);
  const [immersiveLoading, setImmersiveLoading] = useState(false);
  const [immersiveLoadProgress, setImmersiveLoadProgress] = useState(immersiveWorldCache ? 100 : 0);
  const [immersiveLoadStage, setImmersiveLoadStage] = useState(immersiveWorldCache ? 'High-detail geography ready' : 'Waiting');
  const [immersiveError, setImmersiveError] = useState('');
  const [isGlobeFullscreen, setIsGlobeFullscreen] = useState(false);
  const [autoRotate, setAutoRotate] = useState(false);
  const [rotation, setRotation] = useState<[number, number, number]>([-20, -18, 0]);
  const [scale, setScale] = useState(250);
  const [hoveredActorId, setHoveredActorId] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const dragStart = useRef<{ x: number; y: number; rotation: [number, number, number] } | null>(null);
  const globeCardRef = useRef<HTMLDivElement | null>(null);
  const rotationFrame = useRef<number | null>(null);
  const queuedRotation = useRef<[number, number, number] | null>(null);

  const loadLandscape = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await apiFetch<LandscapeResponse>('/threat-intel/landscape');
      setSummary(response.summary);
      setActors(response.actors);
      setSyncInfo(response.sync ?? null);
      setNotice(response.notice ?? '');
      let requestedActor = '';
      try {
        requestedActor = localStorage.getItem('ks-threat-landscape-focus-actor') ?? '';
        if (requestedActor) localStorage.removeItem('ks-threat-landscape-focus-actor');
      } catch { /* local storage is a convenience only */ }
      setSelectedId((current) => {
        if (requestedActor && response.actors.some((actor) => actor.id === requestedActor || actor.mitreId === requestedActor)) {
          return response.actors.find((actor) => actor.id === requestedActor || actor.mitreId === requestedActor)?.id ?? requestedActor;
        }
        return current && response.actors.some((actor) => actor.id === current) ? current : (response.actors[0]?.id ?? '');
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void loadLandscape(); }, [loadLandscape]);

  useEffect(() => {
    if (!canImmersiveGlobe && globeMode === 'immersive') setGlobeMode('standard');
  }, [canImmersiveGlobe, globeMode]);

  useEffect(() => {
    localStorage.setItem('ks-threat-landscape-globe-mode', globeMode);
    if (globeMode !== 'immersive' || immersiveWorld) return;

    let cancelled = false;
    let progressTimer: number | null = null;

    setImmersiveLoading(true);
    setImmersiveError('');
    setImmersiveLoadProgress(8);
    setImmersiveLoadStage('Preparing immersive canvas');

    progressTimer = window.setInterval(() => {
      setImmersiveLoadProgress((current) => {
        if (current < 28) return Math.min(28, current + 4);
        if (current < 68) return Math.min(68, current + 2);
        return current;
      });
    }, 140);

    void loadImmersiveWorld()
      .then((world) => {
        if (cancelled) return;
        setImmersiveLoadStage('Optimizing high-detail geography');
        setImmersiveLoadProgress(82);
        window.requestAnimationFrame(() => {
          if (cancelled) return;
          setImmersiveLoadStage('Preparing actor overlays');
          setImmersiveLoadProgress(96);
          // Complete the loading state before publishing immersiveWorld. Setting
          // immersiveWorld triggers a render; finishing first prevents effect
          // cleanup from leaving the progress state stuck.
          setImmersiveLoadProgress(100);
          setImmersiveLoadStage('Immersive globe ready');
          setImmersiveLoading(false);
          setImmersiveWorld(world);
        });
      })
      .catch((e) => {
        if (cancelled) return;
        setImmersiveError(e instanceof Error ? e.message : 'Could not load high-detail geography.');
        setImmersiveLoadStage('High-detail geography unavailable');
        setImmersiveLoading(false);
      })
      .finally(() => {
        if (progressTimer !== null) window.clearInterval(progressTimer);
      });

    return () => {
      cancelled = true;
      if (progressTimer !== null) window.clearInterval(progressTimer);
    };
  }, [globeMode, immersiveWorld]);

  // Fullscreen is intentionally scoped to the globe card rather than the
  // entire Threat Landscape blade. Escape exits through the native browser API.
  useEffect(() => {
    const onFullscreenChange = () => setIsGlobeFullscreen(document.fullscreenElement === globeCardRef.current);
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  useEffect(() => () => {
    if (rotationFrame.current !== null) window.cancelAnimationFrame(rotationFrame.current);
  }, []);


  useEffect(() => {
    if (!selectedId || !canDossier) {
      setActorDetail(null);
      setRelationships(null);
      setSources([]);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setError('');
    void (async () => {
      try {
        const detail = await apiFetch<ActorResponse>(`/threat-intel/landscape/actors/${encodeURIComponent(selectedId)}`);
        if (cancelled) return;
        setActorDetail(detail.actor);
        const jobs: Promise<void>[] = [];
        if (canRelationships) {
          jobs.push(apiFetch<RelationshipsResponse>(`/threat-intel/landscape/actors/${encodeURIComponent(selectedId)}/relationships`).then((value) => {
            if (!cancelled) setRelationships(value);
          }));
        } else {
          setRelationships(null);
        }
        if (canSources && detail.actor.sourceIds.length) {
          const ids = encodeURIComponent(detail.actor.sourceIds.join(','));
          jobs.push(apiFetch<SourcesResponse>(`/threat-intel/landscape/sources?ids=${ids}`).then((value) => {
            if (!cancelled) setSources(value.sources);
          }));
        } else {
          setSources([]);
        }
        await Promise.all(jobs);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedId, canDossier, canRelationships, canSources]);

  useEffect(() => {
    if (!autoRotate || dragging) return;
    const timer = window.setInterval(() => {
      setRotation((current) => [current[0] - 0.55, current[1], current[2]]);
    }, 80);
    return () => window.clearInterval(timer);
  }, [autoRotate, dragging]);

  const nations = useMemo(() => ['All', ...Array.from(new Set(actors.map((actor) => actor.nation))).sort()], [actors]);
  const motivations = useMemo(() => ['All', ...Array.from(new Set(actors.flatMap((actor) => actor.motivations))).sort()], [actors]);

  const filteredActors = useMemo(() => {
    const q = search.trim().toLowerCase();
    return actors.filter((actor) => {
      if (nation !== 'All' && actor.nation !== nation) return false;
      if (motivation !== 'All' && !actor.motivations.includes(motivation)) return false;
      if (!q) return true;
      return [actor.name, actor.mitreId, actor.nation, ...actor.aliases, ...actor.targetSectors, ...actor.targetRegions]
        .some((value) => value.toLowerCase().includes(q));
    });
  }, [actors, motivation, nation, search]);

  const selectedSummary = actors.find((actor) => actor.id === selectedId) ?? null;

  const countryThreatNations = useMemo(() => new Set(filteredActors.filter((actor) => actor.nation !== 'Unknown').map((actor) => actor.nation.toLowerCase())), [filteredActors]);

  const globe = useMemo(() => {
    const interactiveMotion = dragging || autoRotate;
    const projection = geoOrthographic()
      .translate([W / 2, H / 2])
      .scale(scale)
      .rotate(rotation)
      .clipAngle(90)
      // High-detail 50m geometry is only rendered at rest. During drag/auto-
      // rotate we intentionally relax resampling and use 110m geography.
      .precision(globeMode === 'immersive' ? (interactiveMotion ? 1.45 : 0.82) : 0.95);
    const path = geoPath(projection);
    const activeWorld = globeMode === 'immersive' && immersiveWorld && !interactiveMotion ? immersiveWorld : WORLD;
    const countries = activeWorld.features.map((country: CountryFeature, index: number) => {
      const name = country.properties?.name ?? '';
      return {
        key: `${name}-${index}`,
        name,
        d: path(country as unknown as GeoPermissibleObjects) ?? '',
        highlighted: countryThreatNations.has(name.toLowerCase()),
      };
    }).filter((item) => item.d);
    const graticule = path(geoGraticule10() as unknown as GeoPermissibleObjects) ?? '';

    const perNationIndex = new Map<string, number>();
    const markers = filteredActors.map((actor) => {
      if (actor.nation === 'Unknown' || actor.nationCode === 'UN') return null;
      const index = perNationIndex.get(actor.nation) ?? 0;
      perNationIndex.set(actor.nation, index + 1);
      const point = projection([actor.attributionAnchor.lon, actor.attributionAnchor.lat]);
      if (!point) return null;
      // Several actors can share the same country anchor. Small display-only
      // offsets prevent marker overlap without implying precise geography.
      const angle = index * 2.15;
      const radius = index === 0 ? 0 : 11 + Math.floor(index / 5) * 5;
      const center: [number, number] = [-rotation[0], -rotation[1]];
      return {
        actor,
        x: point[0] + Math.cos(angle) * radius,
        y: point[1] + Math.sin(angle) * radius,
        visible: geoDistance([actor.attributionAnchor.lon, actor.attributionAnchor.lat], center) <= Math.PI / 2,
      };
    }).filter((item): item is NonNullable<typeof item> => Boolean(item));

    const arcs: Array<{ key: string; d: string; actorId: string; region: string; color: string; selected: boolean }> = [];
    if (globeMode === 'immersive' && !interactiveMotion) {
      const center: [number, number] = [-rotation[0], -rotation[1]];
      const orderedActors = [...filteredActors].sort((a, b) => Number(b.id === selectedId) - Number(a.id === selectedId));
      let ambientArcCount = 0;
      // Keep enough ambient context to make the immersive relationship layer visually rich
      // without returning to the earlier expensive all-actor animation model.
      const maxAmbientArcs = 32;
      for (const actor of orderedActors) {
        if (actor.nation === 'Unknown' || actor.nationCode === 'UN') continue;
        const isSelected = actor.id === selectedId;
        if (!isSelected && ambientArcCount >= maxAmbientArcs) break;
        const originGeo: [number, number] = [actor.attributionAnchor.lon, actor.attributionAnchor.lat];
        if (geoDistance(originGeo, center) > Math.PI / 2) continue;
        const origin = projection(originGeo);
        if (!origin) continue;
        const regions = isSelected ? actor.targetRegions.slice(0, 6) : actor.targetRegions.slice(0, 1);
        for (const region of regions) {
          const regionGeo = targetRegionAnchor(region);
          if (!regionGeo || geoDistance(regionGeo, center) > Math.PI / 2) continue;
          const target = projection(regionGeo);
          if (!target) continue;
          const mx = (origin[0] + target[0]) / 2;
          const my = (origin[1] + target[1]) / 2;
          const dx = target[0] - origin[0];
          const dy = target[1] - origin[1];
          const bend = Math.min(70, Math.max(22, Math.hypot(dx, dy) * 0.18));
          const length = Math.max(1, Math.hypot(dx, dy));
          const cx = mx - (dy / length) * bend;
          const cy = my + (dx / length) * bend;
          arcs.push({ key: `${actor.id}:${region}`, actorId: actor.id, region, color: nationMarkerClass(actor.nation), selected: isSelected, d: `M ${origin[0].toFixed(1)} ${origin[1].toFixed(1)} Q ${cx.toFixed(1)} ${cy.toFixed(1)} ${target[0].toFixed(1)} ${target[1].toFixed(1)}` });
          if (!isSelected) ambientArcCount += 1;
        }
      }
    }
    return { projection, countries, graticule, markers, arcs, interactiveMotion };
  }, [autoRotate, countryThreatNations, dragging, filteredActors, globeMode, immersiveWorld, rotation, scale, selectedId]);

  const resetGlobe = () => {
    setRotation([-20, -18, 0]);
    setScale(250);
    setAutoRotate(false);
  };

  const focusActor = (actor: LandscapeActorSummary) => {
    setSelectedId(actor.id);
    if (actor.nation !== 'Unknown' && actor.nationCode !== 'UN') {
      setRotation([-actor.attributionAnchor.lon, -actor.attributionAnchor.lat, 0]);
      setScale((current) => Math.max(current, 260));
    }
  };

  const onPointerDown = (event: ReactPointerEvent<SVGSVGElement>) => {
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragging(true);
    dragStart.current = { x: event.clientX, y: event.clientY, rotation };
  };

  const onPointerMove = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (!dragging || !dragStart.current) return;
    const dx = event.clientX - dragStart.current.x;
    const dy = event.clientY - dragStart.current.y;
    queuedRotation.current = [
      dragStart.current.rotation[0] + dx * 0.34,
      Math.max(-75, Math.min(75, dragStart.current.rotation[1] - dy * 0.34)),
      0,
    ];
    if (rotationFrame.current !== null) return;
    rotationFrame.current = window.requestAnimationFrame(() => {
      rotationFrame.current = null;
      if (queuedRotation.current) setRotation(queuedRotation.current);
    });
  };

  const onPointerUp = (event: ReactPointerEvent<SVGSVGElement>) => {
    if (event.currentTarget.hasPointerCapture(event.pointerId)) event.currentTarget.releasePointerCapture(event.pointerId);
    if (queuedRotation.current) setRotation(queuedRotation.current);
    queuedRotation.current = null;
    setDragging(false);
    dragStart.current = null;
  };

  const toggleGlobeFullscreen = async () => {
    try {
      if (document.fullscreenElement === globeCardRef.current) {
        await document.exitFullscreen();
        return;
      }
      if (document.fullscreenElement) await document.exitFullscreen();
      await globeCardRef.current?.requestFullscreen();
    } catch (e) {
      setImmersiveError(e instanceof Error ? e.message : 'The browser could not enter fullscreen mode.');
    }
  };


  if (loading) {
    return <div className="rounded-2xl border border-border bg-card p-8 text-sm text-muted-foreground">Loading Threat Landscape…</div>;
  }

  return (
    <div className="space-y-5">
      {error && <div className="rounded-xl border border-rose-400/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-100">{error}</div>}

      <div className="rounded-2xl border border-cyan-400/20 bg-gradient-to-br from-slate-950 via-slate-950 to-cyan-950/40 p-5 shadow-xl">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <div className="flex items-center gap-2 text-xs font-black uppercase tracking-[0.2em] text-cyan-300"><Globe2 size={15} /> KloudSecure Threat Landscape</div>
            <h3 className="mt-2 text-2xl font-black text-white">Fast by default · immersive when the analyst wants it</h3>
            <p className="mt-2 max-w-4xl text-sm leading-6 text-blue-100/75">Standard mode stays the default lightweight SVG experience. Optional Immersive mode lazy-loads higher-detail geography and adds atmosphere, glow and animated target-region relationship arcs without blocking normal page scrolling.</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {canImmersiveGlobe && <div className="inline-flex rounded-xl border border-white/10 bg-white/5 p-1">
              <button type="button" onClick={() => setGlobeMode('standard')} className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-black ${globeMode === 'standard' ? 'bg-cyan-500 text-slate-950' : 'text-blue-100 hover:bg-white/10'}`}><Layers3 size={13} /> Standard</button>
              <button type="button" onPointerEnter={() => { void loadImmersiveWorld().catch(() => undefined); }} onFocus={() => { void loadImmersiveWorld().catch(() => undefined); }} onClick={() => setGlobeMode('immersive')} className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-black ${globeMode === 'immersive' ? 'bg-violet-400 text-slate-950' : 'text-blue-100 hover:bg-white/10'}`}><Sparkles size={13} /> Immersive</button>
            </div>}
            <button type="button" onClick={() => setAutoRotate((value) => !value)} className="inline-flex items-center gap-2 rounded-xl border border-cyan-400/25 bg-cyan-500/10 px-3 py-2 text-xs font-bold text-cyan-100 hover:bg-cyan-500/20">
              {autoRotate ? <Pause size={14} /> : <Play size={14} />}{autoRotate ? 'Pause rotation' : 'Auto rotate'}
            </button>
            <button type="button" onClick={() => setScale((current) => Math.max(MIN_SCALE, current - 24))} className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-bold text-blue-100 hover:bg-white/10"><ZoomOut size={14} /> Zoom out</button>
            <button type="button" onClick={() => setScale((current) => Math.min(MAX_SCALE, current + 24))} className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-bold text-blue-100 hover:bg-white/10"><ZoomIn size={14} /> Zoom in</button>
            <button type="button" onClick={resetGlobe} className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-2 text-xs font-bold text-blue-100 hover:bg-white/10"><RefreshCw size={14} /> Reset globe</button>
          </div>
        </div>
        <div className="mt-4 rounded-xl border border-amber-300/20 bg-amber-500/10 px-4 py-3 text-xs leading-5 text-amber-100">
          <b>Attribution note:</b> globe markers represent country-level public attribution anchors only. They are intentionally not operator, server, victim or physical-location coordinates.
        </div>
        {immersiveError && <div className="mt-3 rounded-xl border border-rose-400/25 bg-rose-500/10 px-4 py-3 text-xs text-rose-100">Immersive globe could not load its high-detail geography: {immersiveError}. Standard mode remains available.</div>}
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4 xl:grid-cols-8">
        <StatCard label="Tracked actors" value={summary?.trackedActors ?? actors.length} icon={Shield} />
        <StatCard label="Attributed nations" value={summary?.attributedNations ?? 0} icon={Globe2} />
        <StatCard label="ATT&CK techniques" value={summary?.uniqueTechniques ?? 0} icon={Target} />
        <StatCard label="Malware / tooling" value={summary?.softwareRelationships ?? 0} icon={Activity} />
        <StatCard label="Campaigns" value={summary?.campaigns ?? 0} icon={Waypoints} />
        <StatCard label="Actor CVE links" value={summary?.cveRelationships ?? 0} icon={CircleDot} />
        <StatCard label="Source records" value={summary?.sourceRecords ?? 0} icon={BookOpen} />
        <StatCard label="Raw STIX objects" value={summary?.rawStixObjects ?? 0} icon={Activity} />
      </div>

      <IntelligenceHealth summary={summary} sync={syncInfo} onRefresh={() => void loadLandscape()} />

      <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center">
          <div className="relative min-w-0 flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search actor, alias, sector or target region…" className="w-full rounded-xl border border-border bg-background py-2.5 pl-9 pr-3 text-sm text-foreground outline-none focus:border-cyan-500/60" />
          </div>
          <FilterSelect label="Nation" value={nation} values={nations} onChange={setNation} />
          <FilterSelect label="Motivation" value={motivation} values={motivations} onChange={setMotivation} />
          <div className="rounded-xl border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground"><b className="text-foreground">{filteredActors.length}</b> actors visible</div>
        </div>
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.65fr)_minmax(360px,0.85fr)]">
        <div ref={globeCardRef} className={`overflow-hidden rounded-2xl border border-cyan-400/20 bg-[#020817] shadow-2xl ${isGlobeFullscreen ? 'flex h-screen w-screen flex-col rounded-none border-0' : ''}`}>
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 bg-slate-950/85 px-4 py-3 backdrop-blur-xl">
            <div>
              <div className="flex items-center gap-2 text-sm font-black text-white"><Globe2 size={15} className={globeMode === 'immersive' ? 'text-violet-300' : 'text-cyan-300'} /> Global actor view · {globeMode === 'immersive' ? 'Immersive' : 'Standard'}</div>
              <div className="mt-0.5 text-xs text-blue-100/55">{globeMode === 'immersive' ? 'Adaptive detail · 50m geography at rest · lightweight geometry while moving · focused relationship animation' : '110m lightweight geography · minimal effects · auto-rotate off by default'}</div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {isGlobeFullscreen && <>
                <button type="button" onClick={() => setAutoRotate((value) => !value)} className="inline-flex items-center gap-1.5 rounded-lg border border-white/10 bg-white/5 px-2.5 py-1.5 text-[10px] font-black text-blue-100 hover:bg-white/10">{autoRotate ? <Pause size={12} /> : <Play size={12} />}{autoRotate ? 'Pause' : 'Rotate'}</button>
                <button type="button" onClick={() => setScale((current) => Math.max(MIN_SCALE, current - 24))} className="rounded-lg border border-white/10 bg-white/5 p-1.5 text-blue-100 hover:bg-white/10" aria-label="Zoom out"><ZoomOut size={13} /></button>
                <button type="button" onClick={() => setScale((current) => Math.min(MAX_SCALE, current + 24))} className="rounded-lg border border-white/10 bg-white/5 p-1.5 text-blue-100 hover:bg-white/10" aria-label="Zoom in"><ZoomIn size={13} /></button>
                <button type="button" onClick={resetGlobe} className="rounded-lg border border-white/10 bg-white/5 p-1.5 text-blue-100 hover:bg-white/10" aria-label="Reset globe"><RefreshCw size={13} /></button>
              </>}
              <div className={`rounded-lg border px-2 py-1 text-[10px] font-bold uppercase tracking-wide ${globeMode === 'immersive' ? 'border-violet-400/25 bg-violet-500/10 text-violet-200' : 'border-white/10 bg-white/5 text-blue-100/70'}`}>{globeMode === 'immersive' ? (immersiveLoading ? `${immersiveLoadProgress}%` : 'Ready · 50m') : 'Standard · 110m'}</div>
              <button type="button" onClick={() => { void toggleGlobeFullscreen(); }} className="inline-flex items-center gap-1.5 rounded-lg border border-cyan-400/20 bg-cyan-500/10 px-2.5 py-1.5 text-[10px] font-black uppercase tracking-wide text-cyan-100 hover:bg-cyan-500/20">{isGlobeFullscreen ? <Minimize2 size={12} /> : <Maximize2 size={12} />}{isGlobeFullscreen ? 'Exit full screen' : 'Maximize'}</button>
            </div>
          </div>
          {globeMode === 'immersive' && immersiveLoading && <div className="border-b border-violet-400/15 bg-gradient-to-r from-violet-950/80 via-slate-950 to-cyan-950/70 px-4 py-3">
            <div className="flex items-center justify-between gap-3 text-[11px]"><div className="flex items-center gap-2 font-bold text-violet-100"><LoaderCircle size={14} className="animate-spin text-violet-300" />{immersiveLoadStage}</div><span className="font-mono font-black text-cyan-200">{immersiveLoadProgress}%</span></div>
            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-white/10"><div className="h-full rounded-full bg-gradient-to-r from-violet-500 via-fuchsia-400 to-cyan-400 transition-[width] duration-300" style={{ width: `${immersiveLoadProgress}%` }} /></div>
            <div className="mt-1.5 text-[10px] text-blue-100/45">The globe remains interactive while the high-detail layer is prepared. Loading completes once, then remains cached for this browser session.</div>
          </div>}
          <div className={`relative select-none overflow-hidden ${isGlobeFullscreen ? 'min-h-0 flex-1' : ''}`}>
            <svg
              viewBox={`0 0 ${W} ${H}`}
              className={`${isGlobeFullscreen ? 'h-full w-full' : 'block w-full'} touch-none ${dragging ? 'cursor-grabbing' : 'cursor-grab'}`}
              role="img"
              aria-label="Interactive orthographic globe showing country-level attribution anchors for selected threat actors"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            >
              <defs>
                <radialGradient id="ks-ocean" cx="38%" cy="32%" r="72%">
                  <stop offset="0%" stopColor="#0c3152" />
                  <stop offset="62%" stopColor="#05182b" />
                  <stop offset="100%" stopColor="#01060e" />
                </radialGradient>
                <radialGradient id="ks-atmosphere" cx="50%" cy="50%" r="50%">
                  <stop offset="72%" stopColor="#0ea5e9" stopOpacity="0" />
                  <stop offset="91%" stopColor="#0ea5e9" stopOpacity={globeMode === 'immersive' ? '0.30' : '0.18'} />
                  <stop offset="100%" stopColor={globeMode === 'immersive' ? '#8b5cf6' : '#38bdf8'} stopOpacity={globeMode === 'immersive' ? '0.72' : '0.52'} />
                </radialGradient>
                <linearGradient id="ks-immersive-land" x1="0" y1="0" x2="1" y2="1"><stop offset="0%" stopColor="#18354a" /><stop offset="45%" stopColor="#162a33" /><stop offset="100%" stopColor="#10211f" /></linearGradient>
                <radialGradient id="ks-immersive-ocean" cx="35%" cy="28%" r="78%"><stop offset="0%" stopColor="#123f69" /><stop offset="48%" stopColor="#082541" /><stop offset="82%" stopColor="#031327" /><stop offset="100%" stopColor="#01050c" /></radialGradient>
                <filter id="ks-soft-glow" x="-80%" y="-80%" width="260%" height="260%"><feGaussianBlur stdDeviation="8" /></filter>
                <filter id="ks-marker-glow" x="-100%" y="-100%" width="300%" height="300%">
                  <feGaussianBlur stdDeviation="4" result="blur" />
                  <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                </filter>
              </defs>
              <rect width={W} height={H} fill={globeMode === 'immersive' ? '#00030a' : '#01050d'} />
              {globeMode === 'immersive' && IMMERSIVE_STARS.map((star, index) => <circle key={`star-${index}`} cx={star.x} cy={star.y} r={star.r} fill="#dbeafe" opacity={star.opacity} />)}
              {globeMode === 'immersive' && <>
                <circle cx={W / 2} cy={H / 2} r={scale + 42} fill="none" stroke="#8b5cf6" strokeWidth="1" opacity="0.14" />
                <circle cx={W / 2} cy={H / 2} r={scale + 34} fill="none" stroke="#22d3ee" strokeWidth="0.6" strokeDasharray="2 9" opacity="0.20" />
                <circle cx={W / 2} cy={H / 2} r={scale + 30} fill="#4f46e5" opacity="0.10" filter="url(#ks-soft-glow)" />
              </>}
              <circle cx={W / 2} cy={H / 2} r={scale + (globeMode === 'immersive' ? 22 : 15)} fill="url(#ks-atmosphere)" />
              <circle cx={W / 2} cy={H / 2} r={scale} fill={globeMode === 'immersive' ? 'url(#ks-immersive-ocean)' : 'url(#ks-ocean)'} stroke={globeMode === 'immersive' ? '#6366f1' : '#0b5a8f'} strokeWidth={globeMode === 'immersive' ? '1.8' : '1.4'} />
              {globeMode === 'immersive' && <ellipse cx={W / 2 - scale * 0.28} cy={H / 2 - scale * 0.30} rx={scale * 0.42} ry={scale * 0.23} fill="#67e8f9" opacity="0.045" transform={`rotate(-26 ${W / 2} ${H / 2})`} />}
              <path d={globe.graticule} fill="none" stroke={globeMode === 'immersive' ? '#27476b' : GRATICULE_STROKE} strokeWidth={globeMode === 'immersive' ? '0.55' : '0.45'} opacity={globeMode === 'immersive' ? '0.68' : '0.55'} />
              {globe.countries.map((country) => (
                <path key={country.key} d={country.d} fill={country.highlighted ? (globeMode === 'immersive' ? '#164e63' : NATION_FILL) : (globeMode === 'immersive' ? 'url(#ks-immersive-land)' : COUNTRY_FILL)} stroke={globeMode === 'immersive' ? '#35556e' : COUNTRY_STROKE} strokeWidth={globeMode === 'immersive' ? '0.48' : '0.55'}><title>{country.name}</title></path>
              ))}
              {globeMode === 'immersive' && globe.arcs.map((arc) => (
                <g key={arc.key} pointerEvents="none">
                  {/* Soft underlay gives the dashed relationship path enough contrast over land/ocean
                      without adding filters/animations to every ambient arc. */}
                  <path
                    d={arc.d}
                    fill="none"
                    stroke={arc.color}
                    strokeWidth={arc.selected ? 5.2 : 3.2}
                    opacity={arc.selected ? 0.16 : 0.09}
                    strokeLinecap="round"
                  />
                  <path
                    d={arc.d}
                    fill="none"
                    stroke={arc.color}
                    strokeWidth={arc.selected ? 2.8 : 1.45}
                    opacity={arc.selected ? 0.96 : 0.52}
                    strokeDasharray={arc.selected ? '10 7' : '7 8'}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    filter={arc.selected ? 'url(#ks-marker-glow)' : undefined}
                  >
                    <title>{arc.region} · source-backed target-region relationship, not an observed attack path</title>
                    {arc.selected && <animate attributeName="stroke-dashoffset" from="0" to="-34" dur="2.2s" repeatCount="indefinite" />}
                  </path>
                </g>
              ))}
              {globe.markers.map(({ actor, x, y, visible }) => visible ? (
                <g key={actor.id} transform={`translate(${x} ${y})`} onPointerDown={(e) => e.stopPropagation()} onClick={() => focusActor(actor)} onMouseEnter={() => setHoveredActorId(actor.id)} onMouseLeave={() => setHoveredActorId(null)} className="cursor-pointer">
                  <circle r={selectedId === actor.id ? 14 : 10} fill={nationMarkerClass(actor.nation)} opacity={globeMode === 'immersive' ? '0.24' : '0.15'} filter={selectedId === actor.id ? 'url(#ks-marker-glow)' : undefined}>{globeMode === 'immersive' && selectedId === actor.id && <animate attributeName="r" values="12;18;12" dur="1.9s" repeatCount="indefinite" />}</circle>
                  <circle r={selectedId === actor.id ? 7 : 5.5} fill={nationMarkerClass(actor.nation)} stroke={selectedId === actor.id ? '#ffffff' : '#cbd5e1'} strokeWidth={selectedId === actor.id ? 2 : 1.2} />
                  {selectedId === actor.id && <circle r="11" fill="none" stroke="#ffffff" strokeWidth="0.7" strokeDasharray="2 3" />}
                  {(hoveredActorId === actor.id || selectedId === actor.id) && (
                    <g transform="translate(12 -12)">
                      <rect x="0" y="-16" width={Math.max(86, actor.name.length * 7 + 25)} height="31" rx="8" fill="#020817" stroke="#334155" opacity="0.96" />
                      <text x="9" y="-2" fontSize="11" fontWeight="700" fill="#f8fafc">{actor.name}</text>
                      <text x="9" y="10" fontSize="8.5" fill="#94a3b8">{actor.nation} · {actor.mitreId}</text>
                    </g>
                  )}
                </g>
              ) : null)}
            </svg>
            <div className="pointer-events-none absolute bottom-3 left-3 max-w-[72%] rounded-xl border border-white/10 bg-slate-950/80 px-3 py-2 text-[10px] leading-4 text-blue-100/60 shadow-lg backdrop-blur">{globeMode === 'immersive' ? `Drag = rotate · ${globe.interactiveMotion ? 'adaptive 110m interaction detail' : '50m detail at rest'} · arcs = source-backed target-region relationships, not observed attack paths` : 'Drag = rotate · page scrolling stays native · use buttons to zoom · markers are country-level attribution anchors'}</div>
            {globeMode === 'immersive' && <div className="pointer-events-none absolute right-3 top-3 rounded-xl border border-violet-300/15 bg-slate-950/70 px-3 py-2 text-right shadow-xl backdrop-blur-xl">
              <div className="text-[9px] font-black uppercase tracking-[0.18em] text-violet-200/70">Immersive intelligence canvas</div>
              <div className="mt-1 text-xs font-black text-white">{filteredActors.length} actors · {globe.markers.filter((m) => m.visible).length} visible anchors</div>
              <div className="mt-0.5 text-[9px] text-cyan-100/45">Bold dashed relationships · selected actor paths animate · ambient paths stay static for performance</div>
            </div>}
          </div>
        </div>

        <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="text-xs font-black uppercase tracking-[0.16em] text-muted-foreground">Actor selector</div>
              <div className="mt-1 text-lg font-black text-foreground">{selectedSummary?.name ?? 'Choose an actor'}</div>
            </div>
            <Waypoints size={20} className="text-cyan-500" />
          </div>
          <div className="mt-4 max-h-[520px] space-y-2 overflow-auto pr-1">
            {filteredActors.map((actor) => {
              const selected = actor.id === selectedId;
              return (
                <button key={actor.id} type="button" onClick={() => focusActor(actor)} className={`w-full rounded-xl border p-3 text-left transition ${selected ? 'border-cyan-400/40 bg-cyan-500/10' : 'border-border bg-background hover:bg-muted'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="font-black text-foreground">{actor.name}</div>
                      <div className="mt-0.5 text-xs text-muted-foreground">{actor.nation} · {actor.mitreId}</div>
                    </div>
                    <span className="rounded-full border border-emerald-400/25 bg-emerald-500/10 px-2 py-0.5 text-[10px] font-black text-emerald-200">{actor.confidence}</span>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">{actor.motivations.map((item) => <span key={item} className={`rounded-full border px-2 py-0.5 text-[10px] font-bold ${motifClass(item)}`}>{item}</span>)}</div>
                  <div className="mt-2 text-xs leading-5 text-muted-foreground">{shortText(actor.summary, 125)}</div>
                  <div className="mt-2 flex gap-3 text-[10px] font-semibold text-muted-foreground"><span>{actor.techniqueCount} ATT&CK</span><span>{actor.softwareCount} software</span><span>{actor.cveCount} CVEs</span></div>
                </button>
              );
            })}
            {!filteredActors.length && <div className="rounded-xl border border-dashed border-border p-5 text-center text-sm text-muted-foreground">No actors match the current filters.</div>}
          </div>
        </div>
      </div>

      {selectedSummary && !canDossier && <PermissionNotice capability="threat_intel.view_actor_dossier" text="The globe is available, but your role cannot open detailed actor dossiers." />}

      {selectedSummary && canDossier && (
        <div className="space-y-5 rounded-2xl border border-border bg-card p-5 shadow-sm">
          {detailLoading && <div className="text-sm text-muted-foreground">Loading actor dossier…</div>}
          {actorDetail && (
            <>
              <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                <div className="max-w-4xl">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded-full border border-cyan-400/25 bg-cyan-500/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-wide text-cyan-200">Actor dossier</span>
                    <span className="rounded-full border border-border bg-muted/40 px-2.5 py-1 text-[10px] font-black text-muted-foreground">MITRE {actorDetail.mitreId}</span>
                    <span className="rounded-full border border-emerald-400/25 bg-emerald-500/10 px-2.5 py-1 text-[10px] font-black text-emerald-200">Confidence {actorDetail.confidence}</span>
                  </div>
                  <h3 className="mt-3 text-2xl font-black text-foreground">{actorDetail.name}</h3>
                  <p className="mt-2 text-sm leading-6 text-muted-foreground">{actorDetail.summary}</p>
                </div>
                <div className="rounded-xl border border-border bg-muted/20 p-3 text-xs text-muted-foreground">
                  <div><b className="text-foreground">Nation:</b> {actorDetail.nation}</div>
                  <div className="mt-1"><b className="text-foreground">Reviewed:</b> {fmtDate(actorDetail.lastReviewedUtc)}</div>
                  <div className="mt-1"><b className="text-foreground">Anchor:</b> {actorDetail.attributionAnchor.label}</div>
                </div>
              </div>

              <div className="grid gap-4 lg:grid-cols-2">
                <DossierList title="Aliases" items={actorDetail.aliases} />
                <DossierList title="Motivations" items={actorDetail.motivations} />
                <DossierList title="Target sectors" items={actorDetail.targetSectors} />
                <DossierList title="Target regions" items={actorDetail.targetRegions} />
              </div>

              <ThreatLandscapeOperationalPanel actor={actorDetail} />

              {(canIntelligenceGraph || canActorCampaigns || canThreatFeed) && <ThreatLandscapeIntelligenceGraph actor={{ id: actorDetail.id, name: actorDetail.name, mitreId: actorDetail.mitreId }} />}

              {!canIntelligenceGraph && (canRelationships ? (
                relationships && <RelationshipBoard actor={actorDetail} relationships={relationships} />
              ) : <PermissionNotice capability="threat_intel.view_threat_relationships" text="Relationship data is hidden by Permissions Master." />)}

              {canSources ? (
                <SourceAttribution sources={sources} />
              ) : <PermissionNotice capability="threat_intel.view_source_attribution" text="Source attribution links are hidden by Permissions Master." />}
            </>
          )}
        </div>
      )}

      <div className="rounded-xl border border-border bg-muted/20 px-4 py-3 text-xs text-muted-foreground">
        <b className="text-foreground">Threat Landscape data model:</b> MITRE ATT&CK TAXII 2.1 is the canonical synchronized actor source; CISA KEV, Microsoft Threat Intelligence and the existing KloudSecure vulnerability-intelligence cache are layered server-side. PostgreSQL stores normalized/runtime data and the VM keeps raw latest-source snapshots for fallback. The browser receives only lightweight actor summaries and selected dossier/operational results. Standard globe mode remains the default; Immersive mode lazy-loads its higher-detail geography only when explicitly selected. Phase 3 adds bounded server-side Intelligence Graph, campaign timeline and source-backed intelligence feed views without shipping raw STIX to the browser. {notice && <span className="ml-1">{notice}</span>}
      </div>
    </div>
  );
}

function syncStatusClass(status: string): string {
  const value = status.toLowerCase();
  if (value === 'ok') return 'border-emerald-400/25 bg-emerald-500/10 text-emerald-200';
  if (value === 'warning') return 'border-amber-400/25 bg-amber-500/10 text-amber-200';
  if (value === 'error') return 'border-rose-400/25 bg-rose-500/10 text-rose-200';
  return 'border-border bg-muted/30 text-muted-foreground';
}

function IntelligenceHealth({ summary, sync, onRefresh }: { summary: LandscapeSummary | null; sync: LandscapeSyncInfo | null; onRefresh: () => void }) {
  return (
    <div className="rounded-2xl border border-cyan-400/15 bg-card p-4 shadow-sm">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div>
          <div className="text-xs font-black uppercase tracking-[0.16em] text-cyan-600 dark:text-cyan-300">Intelligence health</div>
          <div className="mt-1 text-sm font-bold text-foreground">
            {summary?.datasetMode === 'synchronized_postgres' ? 'Synchronized server-side intelligence' : 'Curated seed fallback'}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            Last successful sync: {fmtDate(sync?.lastSuccessfulSync ?? summary?.lastReviewedUtc)}
            {summary?.cisaKevRecords ? ` · ${summary.cisaKevRecords} CISA KEV records` : ''}
            {summary?.microsoftArticles ? ` · ${summary.microsoftArticles} matched Microsoft articles` : ''}
            {summary?.kloudSecureVulnerabilities ? ` · ${summary.kloudSecureVulnerabilities} KloudSecure CVE cache records` : ''}
          </div>
        </div>
        <button type="button" onClick={onRefresh} className="inline-flex items-center gap-2 self-start rounded-xl border border-border bg-background px-3 py-2 text-xs font-bold text-foreground hover:bg-muted">
          <RefreshCw size={14} /> Refresh view
        </button>
      </div>
      <div className="mt-3 grid gap-2 sm:grid-cols-2 xl:grid-cols-4">
        {(sync?.sources ?? []).map((source) => (
          <div key={source.sourceKey} className={`rounded-xl border px-3 py-2 ${syncStatusClass(source.status)}`}>
            <div className="flex items-center justify-between gap-2">
              <span className="text-xs font-black">{source.displayName}</span>
              <span className="text-[10px] font-black uppercase">{source.status}</span>
            </div>
            <div className="mt-1 text-[10px] opacity-80">{source.objectCount.toLocaleString()} objects · {source.mode || 'default'}</div>
            {source.lastError && <div className="mt-1 text-[10px] opacity-80">{shortText(source.lastError, 120)}</div>}
          </div>
        ))}
        {!sync?.sources?.length && (
          <div className="rounded-xl border border-dashed border-border px-3 py-3 text-xs text-muted-foreground">
            Source sync state will appear after the first background/manual synchronization.
          </div>
        )}
      </div>
    </div>
  );
}

function StatCard({ label, value, icon: Icon }: { label: string; value: number; icon: typeof Shield }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-sm">
      <div className="flex items-center justify-between gap-2"><span className="text-[10px] font-black uppercase tracking-wide text-muted-foreground">{label}</span><Icon size={15} className="text-cyan-500" /></div>
      <div className="mt-2 text-2xl font-black text-foreground">{value}</div>
    </div>
  );
}

function FilterSelect({ label, value, values, onChange }: { label: string; value: string; values: string[]; onChange: (value: string) => void }) {
  return (
    <label className="flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2 text-xs">
      <Filter size={13} className="text-muted-foreground" />
      <span className="font-semibold text-muted-foreground">{label}</span>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="bg-transparent font-semibold text-foreground outline-none">
        {values.map((item) => <option key={item} value={item}>{item}</option>)}
      </select>
    </label>
  );
}

function DossierList({ title, items }: { title: string; items: string[] }) {
  return (
    <div className="rounded-xl border border-border bg-background p-4">
      <div className="text-xs font-black uppercase tracking-wide text-muted-foreground">{title}</div>
      <div className="mt-3 flex flex-wrap gap-2">{items.map((item) => <span key={item} className="rounded-lg border border-border bg-muted/30 px-2.5 py-1.5 text-xs font-semibold text-foreground">{item}</span>)}</div>
    </div>
  );
}

function RelationshipBoard({ actor, relationships }: { actor: ActorDetail; relationships: RelationshipsResponse }) {
  return (
    <div className="rounded-2xl border border-cyan-400/20 bg-slate-950 p-4">
      <div className="flex items-center gap-2"><Waypoints size={17} className="text-cyan-400" /><div className="text-sm font-black text-white">Relationship view</div></div>
      <p className="mt-1 text-xs text-blue-100/60">A deterministic relationship board replaces a force-directed graph in Phase 1, avoiding a physics loop while keeping pivots obvious.</p>
      <div className="mt-4 grid gap-3 xl:grid-cols-[0.75fr_1fr_1fr_1fr]">
        <div className="flex min-h-36 items-center justify-center rounded-xl border border-cyan-400/30 bg-cyan-500/10 p-4 text-center">
          <div><Target size={24} className="mx-auto text-cyan-300" /><div className="mt-2 font-black text-white">{actor.name}</div><div className="text-xs text-cyan-100/70">{actor.mitreId}</div></div>
        </div>
        <RelationColumn title="MITRE ATT&CK" items={relationships.techniques.map((t) => ({ primary: t.id, secondary: t.name }))} empty="No curated ATT&CK relationships." />
        <RelationColumn title="Malware / tooling" items={relationships.software.map((s) => ({ primary: s.name, secondary: s.kind }))} empty="No curated software relationships." />
        <RelationColumn title="CVEs" items={relationships.cves.map((c) => ({ primary: c.id, secondary: c.product }))} empty="No curated CVE relationship in the Phase 1 seed catalog." />
      </div>
      {relationships.cves.length > 0 && (
        <div className="mt-3 space-y-2">{relationships.cves.map((cve) => <div key={cve.id} className="rounded-xl border border-amber-400/20 bg-amber-500/10 px-3 py-2 text-xs text-amber-100"><b>{cve.id}</b> · {cve.note}</div>)}</div>
      )}
    </div>
  );
}

function RelationColumn({ title, items, empty }: { title: string; items: Array<{ primary: string; secondary: string }>; empty: string }) {
  return (
    <div className="rounded-xl border border-white/10 bg-white/5 p-3">
      <div className="text-[10px] font-black uppercase tracking-wide text-blue-100/60">{title}</div>
      <div className="mt-2 space-y-2">
        {items.map((item) => <div key={`${item.primary}-${item.secondary}`} className="rounded-lg border border-white/10 bg-slate-950/60 p-2.5"><div className="font-mono text-xs font-black text-cyan-200">{item.primary}</div><div className="mt-0.5 text-[11px] text-blue-100/65">{item.secondary}</div></div>)}
        {!items.length && <div className="rounded-lg border border-dashed border-white/10 p-3 text-xs leading-5 text-blue-100/45">{empty}</div>}
      </div>
    </div>
  );
}

function SourceAttribution({ sources }: { sources: LandscapeSource[] }) {
  return (
    <div className="rounded-2xl border border-border bg-background p-4">
      <div className="flex items-center gap-2"><BookOpen size={17} className="text-cyan-500" /><div className="text-sm font-black text-foreground">Source attribution</div></div>
      <p className="mt-1 text-xs text-muted-foreground">Actor claims and the highlighted relationships are tied to public intelligence sources. KloudSecure does not treat the globe marker itself as evidence of precise location.</p>
      <div className="mt-3 grid gap-3 lg:grid-cols-2">
        {sources.map((source) => (
          <a key={source.id} href={source.url} target="_blank" rel="noreferrer" className="group rounded-xl border border-border bg-card p-3 hover:border-cyan-500/40 hover:bg-cyan-500/5">
            <div className="flex items-start justify-between gap-3"><div><div className="text-[10px] font-black uppercase tracking-wide text-cyan-600 dark:text-cyan-300">{source.publisher} · {source.sourceType}</div><div className="mt-1 text-sm font-bold text-foreground">{source.title}</div></div><ExternalLink size={14} className="mt-1 shrink-0 text-muted-foreground group-hover:text-cyan-500" /></div>
            <div className="mt-2 text-[10px] font-semibold text-muted-foreground">{source.tlp}</div>
          </a>
        ))}
        {!sources.length && <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">No source records returned.</div>}
      </div>
    </div>
  );
}

function PermissionNotice({ capability, text }: { capability: string; text: string }) {
  return <div className="rounded-xl border border-amber-400/25 bg-amber-500/10 px-4 py-3 text-xs text-amber-100"><b>{capability}</b> · {text}</div>;
}
